import _ from "lodash";
import React, { useMemo } from "react";
import { FilterLabelButton, ChecklistDropdown } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import classes from "./FilterDropdownV2.scss";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import {
  FILTER_NAME_COURSES,
  FILTER_NAME_CLASSES,
  FILTER_NAME_CREATORS,
} from "Course/Constants/StringConstants";

const ListSkeletonLoader = (props = {}) => {
  const { dummyCount = 2 } = props;
  return (
    <div className={classes.skeletonBlockContainer}>
      {_.times(dummyCount, index => (
        <div key={index} className={classes.skeletonCard}>
          <SkeletonLoader height="20px" width="100%" />
        </div>
      ))}
    </div>
  );
};

const FilterDropdownV2 = ({
  options,
  updateInputField,
  name,
  showSearchBar,
  label,
  value = [],
  type,
  noOptionLabel,
  showAllOption,
  showSelectedOptionsOnTop,
  isDisabled,
  isLocalizedNoOptionLabel = false,
  showArchivedOptionsAtLast = false,
  testId = "UIComponents-filterDropdown",
  handleCourseFilterScrollEnd,
  handleClassFilterScrollEnd,
  handleCreatorFilterScrollEnd,
  isFetchingMoreDataForCourseFilter,
  updateCourseFilterSearchText,
  isCourseListLoading,
  isClassesListLoading,
  isFetchingMoreDataForClasses,
  updateClassFilterSearchText,
  isCreatorListLoading,
  isFetchingMoreDataForCreatorFilter,
  updateCreatorFilterSearchText,
  filterLabelButtonProps = {},
}) => {
  const { t } = useI18n(["common"]);

  // sometimes archived options are not displayed at last
  // handling it here instead of passing sorted options
  // everytime
  const sortedOptions = useMemo(
    () =>
      showArchivedOptionsAtLast ? _.sortBy(options, "isArchived") : options,
    [showArchivedOptionsAtLast, options]
  );

  const handleSearchTextChangeCourseFilter = searchText => {
    updateCourseFilterSearchText(searchText);
  };
  const handleSearchTextChangeClassFilter = searchText => {
    updateClassFilterSearchText(searchText);
  };
  const handleSearchTextChangeCreatorFilter = searchText => {
    updateCreatorFilterSearchText(searchText);
  };

  const clearSearchText = () => {
    updateCourseFilterSearchText("");
  };
  const clearSearchTextForClassFilter = () => {
    updateClassFilterSearchText("");
  };
  const clearSearchTextForCreatorFilter = () => {
    updateCreatorFilterSearchText("");
  };

  const renderDropdown = () => {
    switch (type) {
      case "CHECKLIST_DROPDOWN":
        return (
          <div key={name}>
            <ChecklistDropdown
              key={name}
              testId={`${testId}-${name}`}
              options={sortedOptions}
              value={value}
              onChange={value => updateInputField({ [name]: value })}
              showAllOption={showAllOption}
              isSearchEnabled={showSearchBar}
              showSelectedOptionsOnTop={showSelectedOptionsOnTop}
              disabled={isDisabled}
              {...(name === FILTER_NAME_COURSES && {
                loader: (isCourseListLoading ||
                  isFetchingMoreDataForCourseFilter) && <ListSkeletonLoader />,
                onScrollEnd: handleCourseFilterScrollEnd,
                onVisibleChange: clearSearchText,
                onSearchTextChange: handleSearchTextChangeCourseFilter,
              })}
              {...(name === FILTER_NAME_CLASSES && {
                loader: (isClassesListLoading ||
                  isFetchingMoreDataForClasses) && <ListSkeletonLoader />,
                onScrollEnd: handleClassFilterScrollEnd,
                onVisibleChange: clearSearchTextForClassFilter,
                onSearchTextChange: handleSearchTextChangeClassFilter,
              })}
              {...(name === FILTER_NAME_CREATORS && {
                loader: (isCreatorListLoading ||
                  isFetchingMoreDataForCreatorFilter) && <ListSkeletonLoader />,
                onScrollEnd: handleCreatorFilterScrollEnd,
                onVisibleChange: clearSearchTextForCreatorFilter,
                onSearchTextChange: handleSearchTextChangeCreatorFilter,
              })}
            >
              <FilterLabelButton
                _legacy_variant="plain"
                variant="neutral"
                type="plain"
                label={t(label)}
                testId={`${testId}-${name}`}
                labelValue={getLabelValueForMultiFilterLabelButton({
                  options,
                  selectedOptions: _.isNull(value) ? [] : value,
                  t,
                  noneText: isLocalizedNoOptionLabel
                    ? noOptionLabel
                    : t(noOptionLabel),
                  allText: t("common:all"),
                })}
                {...filterLabelButtonProps}
              />
            </ChecklistDropdown>
          </div>
        );
      default:
        return null;
    }
  };
  return renderDropdown();
};

export default FilterDropdownV2;
